import { Autocomplete, Box, Typography, TextField } from "@mui/material"
import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"

import StyledHelperText from "@/components/StyledHelperText"
import { useSavedGuestsQuery } from "@/queries/hooks"

import { PassHolderFormData } from "../../schema"
import { formatUserOption, validateAgeUserOption } from "../../utils"

type FirstNameFieldProps = {
  minAge: number
  maxAge: number
}

const FirstNameField = ({ minAge, maxAge }: FirstNameFieldProps) => {
  const { control, setValue } = useFormContext<PassHolderFormData>()
  const { t } = useTranslation()

  const { data: savedGuests = [] } = useSavedGuestsQuery()
  return (
    <Controller
      name="firstName"
      control={control}
      render={({ field, fieldState: { error } }) =>
        savedGuests.length === 0 ? (
          <TextField
            value={field.value || ""}
            onChange={(e) => field.onChange(e.target.value)}
            name="firstName"
            label={t("passHolderDetails.form.firstName")}
            error={!!error}
            helperText={
              error?.message ? (
                <StyledHelperText helperText={error.message} severity="error" />
              ) : undefined
            }
          />
        ) : (
          <Autocomplete
            freeSolo
            disableClearable
            options={savedGuests}
            value={field.value || ""}
            onChange={(_, value) => {
              if (typeof value === "string") {
                field.onChange(value)
              } else if (value) {
                field.onChange(value.firstName)
                setValue("lastName", value.lastName, {
                  shouldValidate: true,
                })
                setValue("birthDate", value.birthDate, {
                  shouldValidate: true,
                })
              }
            }}
            onInputChange={(_, newInputValue) => {
              field.onChange(newInputValue)
            }}
            getOptionKey={(option) => {
              return typeof option === "string" ? option : option.id
            }}
            getOptionDisabled={(option) => {
              const validationResult = validateAgeUserOption(
                option,
                minAge,
                maxAge
              )
              return !validationResult
            }}
            getOptionLabel={(option) => {
              return formatUserOption(option)
            }}
            renderOption={(props, option) => {
              const validationResult = validateAgeUserOption(
                option,
                minAge,
                maxAge
              )
              const { key, ...otherProps } = props
              return (
                <li key={key} {...otherProps}>
                  <Box>
                    <Typography variant="body1">
                      {formatUserOption(option)}
                    </Typography>
                    {!validationResult && (
                      <Typography variant="body2" color="text.secondary">
                        {t("passHolderDetails.form.ageGroupNotEligible")}
                      </Typography>
                    )}
                  </Box>
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                name="firstName"
                label={t("passHolderDetails.form.firstName")}
                error={!!error}
                helperText={
                  error?.message ? (
                    <StyledHelperText
                      helperText={error.message}
                      severity="error"
                    />
                  ) : undefined
                }
              />
            )}
          />
        )
      }
    />
  )
}

export default FirstNameField
